import { useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext, useApi } from '@repo-level/core-front';
import useEventListener from '@repo-level/core-front/hooks/eventListener';

import { showTooltip } from 'helpers/tooltip';
import { TooltipTypes } from 'modules/globalTooltip';
import { useAnalytics } from 'helpers/analytics';
import { AnalyticsEventName } from 'api/analytics';
import { AddonsContext, lastSeenEmailConfirmationPopup } from 'contexts/addons';
import { translationFactory } from 'helpers/trans';
import { ButtonCross } from 'components/buttons';
import { confirmationEmailPost } from 'api/auth';

import { ScEmailConfirmPopup } from './styled';

export const EmailConfirmPopup = () => {
  const {
    setEmailConfirmationPopupSeen,
    setEmailConfirmationPopupHeight,
  } = useContext(AddonsContext);

  const { user } = useContext(AuthContext);

  const { sendEventToAnalytics } = useAnalytics();

  const [requestsConfirmationEmail] = useApi(confirmationEmailPost);

  const { t } = useTranslation('common');

  const ref = useRef<HTMLDivElement>(null);

  const handleLinkClick = useCallback(async () => {
    if (!user) return;

    const { error } = await requestsConfirmationEmail(user.email).request;

    if (error) return;

    setEmailConfirmationPopupSeen();

    showTooltip({
      type: TooltipTypes.SUCCESS,
      translate: true,
      title: translationFactory('verifyEmailPopup.successTooltip'),
    });
  }, [requestsConfirmationEmail, setEmailConfirmationPopupSeen, user]);

  const handleCloseClick = useCallback(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.EMAIL_CONFIRMATION_POPUP__BUTTON_CLOSE__CLICK,
    });
    setEmailConfirmationPopupSeen();
  }, [sendEventToAnalytics, setEmailConfirmationPopupSeen]);

  useEffect(() => {
    sendEventToAnalytics({
      name:
        lastSeenEmailConfirmationPopup.get() == null
          ? AnalyticsEventName.EMAIL_CONFIRMATION_POPUP__FIRST_APPEARANCE
          : AnalyticsEventName.EMAIL_CONFIRMATION_POPUP__SUBSEQUENT_APPEARANCE,
    });
  }, [sendEventToAnalytics]);

  useEventListener(
    'resize',
    () =>
      ref.current && setEmailConfirmationPopupHeight(ref.current?.clientHeight),
    undefined,
    true
  );

  return (
    <ScEmailConfirmPopup ref={ref}>
      <div className="text">
        {t('verifyEmailPopup.text')}
        <span className="link" onClick={handleLinkClick}>
          {t('verifyEmailPopup.linkText')}
        </span>
      </div>
      <ButtonCross right={16} top={4} small onClick={handleCloseClick} />
    </ScEmailConfirmPopup>
  );
};
